import { v4 } from "uuid";
import { create } from "zustand";
import { SetToggle } from "@/utils/dom";
import type { InstagramSearch } from "../types";

interface InstagramSearchState {
  config: InstagramSearch.Config;

  isAuthorized: boolean;
  isSubscribed: boolean;

  id: string;
  setId: (id: string) => void;

  query: string;

  category: InstagramSearch.Option["id"] | undefined;

  country: InstagramSearch.Option["id"] | undefined;

  regions: SetToggle<InstagramSearch.Option["id"]>;

  cities: SetToggle<InstagramSearch.Option["id"]>;

  gender: InstagramSearch.Option["id"] | undefined;

  followers: InstagramSearch.RangeValue;

  posts: InstagramSearch.RangeValue;

  influencersCategory: InstagramSearch.Option | undefined;
  setInfluencersCategory: (
    influencersCategory: InstagramSearch.Option | undefined,
  ) => void;

  sortingInfluencers: InstagramSearch.Option | undefined;
  setSortingInfluencers: (
    sortingInfluencers: InstagramSearch.Option | undefined,
  ) => void;

  sortingProfiles: InstagramSearch.Option | undefined;
  setSortingProfiles: (
    sortingProfiles: InstagramSearch.Option | undefined,
  ) => void;

  selection: Set<InstagramSearch.Profile["id"]>;
  clearSelection: () => void;
  toggleSelected: (selection: Set<InstagramSearch.Profile["id"]>) => void;
  setSelected: (id: InstagramSearch.Profile["id"][]) => void;

  listsSelection: Set<InstagramSearch.List["id"]>;
  listsClearSelection: () => void;
  listsToggleSelected: (id: InstagramSearch.List["id"]) => void;
  setListsSelected: (id: InstagramSearch.List["id"][]) => void;
}

export const useInstagramSearch = create<InstagramSearchState>((set) => ({
  config: undefined as unknown as InstagramSearch.Config,

  isAuthorized: false,
  isSubscribed: false,

  id: "",
  setId: (id) => set({ id }),

  query: "",

  category: undefined,

  country: undefined,

  regions: new SetToggle(),

  cities: new SetToggle(),

  gender: undefined,

  followers: {
    start: 0,
    end: 0,
  },

  posts: {
    start: 0,
    end: 0,
  },

  influencersCategory: undefined,
  setInfluencersCategory: (influencersCategory) =>
    set({
      influencersCategory,
      id: v4(),
      selection: new Set(),
      listsSelection: new Set(),
    }),

  sortingInfluencers: undefined,
  setSortingInfluencers: (sortingInfluencers) =>
    set({
      sortingInfluencers,
      id: v4(),
      selection: new Set(),
      listsSelection: new Set(),
    }),

  sortingProfiles: undefined,
  setSortingProfiles: (sortingProfiles) =>
    set({
      sortingProfiles,
      id: v4(),
      selection: new Set(),
      listsSelection: new Set(),
    }),

  selection: new Set(),
  clearSelection: () => set({ selection: new Set() }),
  toggleSelected: (selection) => {
    set((state) => {
      if (selection.isSubsetOf(state.selection)) {
        return {
          selection: state.selection.difference(selection),
        };
      }

      return {
        selection: selection.union(state.selection),
      };
    });
  },
  setSelected: (ids) => set({ selection: new Set(ids) }),

  listsSelection: new Set(),
  listsClearSelection: () => set({ listsSelection: new Set() }),
  listsToggleSelected: (id) => {
    set((state) => {
      const listsSelection = new Set(state.listsSelection);

      if (state.listsSelection.has(id)) {
        listsSelection.delete(id);
      } else {
        listsSelection.add(id);
      }

      return {
        listsSelection,
      };
    });
  },
  setListsSelected: (ids) => set({ listsSelection: new Set(ids) }),
}));

export const useInstagramSearchInfluencersFilters = () => {
  const state = useInstagramSearch();

  return getInfluencersFilters(state);
};

export const useInstagramSearchProfilesFilters = () => {
  const state = useInstagramSearch();

  return getProfilesFilters(state);
};

export const getInstagramSearchInfluencersFilters = () => {
  return getInfluencersFilters(useInstagramSearch.getState());
};

export const getInstagramSearchProfilesFilters = () => {
  return getProfilesFilters(useInstagramSearch.getState());
};

const getInfluencersFilters = (state: InstagramSearchState) => {
  return {
    source: "influencers",
    category: state.influencersCategory?.id,
    sorting: state.sortingInfluencers?.id,
  };
};

const getProfilesFilters = (state: InstagramSearchState) => {
  return {
    source: "profiles",
    query: state.query,
    category: state.category,
    country: state.country,
    regions: Array.from(state.regions),
    cities: Array.from(state.cities),
    gender: state.gender,
    followersStart: state.followers.start,
    followersEnd: state.followers.end,
    postsStart: state.posts.start,
    postsEnd: state.posts.end,
    sorting: state.sortingProfiles?.id,
  };
};
